import React, { useRef, useCallback, useEffect, useImperativeHandle } from 'react';
import type { VersionsDropdownProps, VersionsDropdownAPI } from './types';

declare const $: any;

/**
 * Renders an <a href="#"> with a native `onclick` DOM handler.
 * jQuery UI Menu captures events on menu items, so we set the handler
 * via ref as a DOM property (not attribute) to avoid global function
 * collisions when multiple dropdowns are mounted.
 */
function MenuAction({ onAction, children }: { onAction: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLAnchorElement>(null);
  const handlerRef = useRef(onAction);
  handlerRef.current = onAction;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      handlerRef.current();
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, []);
  return <a href="#" ref={ref}>{children}</a>;
}

const VersionsDropdown = React.forwardRef<VersionsDropdownAPI, VersionsDropdownProps>(
  function VersionsDropdown(props, ref) {
    const {
      fileId,
      currentVersionTitle,
      versions,
      linkId,
      onVersionChange,
      shouldShowVersionComparison,
      onVersionComparisonClick,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const compareButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      getContainerElement: () => containerRef.current,
    }), []);

    const handleButtonClick = useCallback(() => {
      if (menuRef.current) {
        $(menuRef.current).show().menu();
      }
    }, []);

    const showVersionComparisonDropdown = useCallback(() => {
      const el = document.createElement('ul');
      el.className = 'menu';

      for (const version of versions) {
          if (!version.isCurrent) {
              const li = document.createElement('li');

              const a = document.createElement('a');
              a.href = '#';
              a.onclick = (e) => {
                  e.preventDefault();
                  onVersionComparisonClick?.(version.id)

                  // Cleanup
                  $(el).menu('destroy');
                  el.remove();
              }
              a.textContent = version.title;

              li.appendChild(a);
              el.appendChild(li);
          }
      }

      // Attach to document first
      (containerRef.current ?? document.body).appendChild(el);
      $(el).show().menu();

      // Calculate position
      if (compareButtonRef.current) {
        const rect = compareButtonRef.current.getBoundingClientRect();

        el.style.left = (compareButtonRef.current.offsetLeft + (rect.width / 2) - (el.clientWidth / 2)) + 'px';
      }

    }, [versions, onVersionComparisonClick]);

      // Don't render if there's only one version (or none)
    if (versions.length <= 1) return null;

    // Build the click handler for a version menu item.
    // If onVersionChange is provided, call it directly (per-column);
    // otherwise fall back to loadFile() for dynamic file switching.
    const buildVersionAction = (targetFileId: number): (() => void) => {
      if (onVersionChange) {
        return () => onVersionChange(targetFileId);
      }
      return () => {
        const w = window as any;
        if (typeof w.loadFile === 'function') w.loadFile(targetFileId);
      };
    };

    return (
        <div ref={containerRef}>
            <button
                onClick={handleButtonClick}
                className="version-selector-button"
                aria-label="Select version"
            >
                {currentVersionTitle} &#x25BE;
            </button>
            {shouldShowVersionComparison && onVersionComparisonClick &&
                <button
                    ref={compareButtonRef}
                    className="version-add-compare-button tooltip"
                    aria-label="Compare version"
                    title="Compare version"
                    onClick={showVersionComparisonDropdown}
                >
                    <div
                        className="fa-icon fa-icon-circle-plus-outline"
                    />
                </button>
            }
            <ul
                id="revision_dropdown"
                ref={menuRef}
                className="menu"
                style={{display: 'none', minWidth: '170px'}}
            >
                {versions.map(ver => (
                    <li key={ver.id} className={ver.isCurrent ? 'ui-checked' : ''}>
                        {ver.isCurrent ? (
                            <a href="#">
                                <img className="file-thumb small" src={ver.thumbUrl}/> {ver.title}
                            </a>
                        ) : (
                            <MenuAction onAction={buildVersionAction(ver.id)}>
                                <img className="file-thumb small" src={ver.thumbUrl}/> {ver.title}
                            </MenuAction>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
  }
);

export default VersionsDropdown;
